import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';
import {
  Table,
  Typography,
  Tag,
  Popover,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';

import { PayrollAccountingStatusesEnum } from '@thinknet-gmbh/platform-common';

import { useCreateDatevPayrollAccountingFile } from 'src/hooks/payroll-accounting/create-datev-file.hook';
import { useUpdateDatevPayrollAccountingFile } from 'src/hooks/payroll-accounting/update-datev-file.hook';

import { PAYROLL_ACCOUNTING_STATUSES_STYLES } from './payroll-accounting-datev-table.constants';
import {
  IDatevPayrollAccountingFilters,
  IPayrollAccounting,
} from '../../../payroll-accounting.interfaces';

const PayrollAccountingDatevTable = ({
  dataSource,
  filters,
}: {
  dataSource: IPayrollAccounting &
    {
      monthDate: Date;
      monthNumber: number;
      month: string;
    }[];
  filters: IDatevPayrollAccountingFilters;
}) => {
  const { t } = useTranslation(['common', 'payrollAccounting']);

  const createFile = useCreateDatevPayrollAccountingFile({});
  const updateFile = useUpdateDatevPayrollAccountingFile({});

  const handleFileCreate = ({ month, _id }: { _id?: string; month: Date }) => () => {
    const func = _id ? updateFile : createFile;
    func({
      variables: {
        payload: {
          ...(_id && { _id }),
          month,
          ...omit(filters, ['type']),
        },
      },
    });
  };

  return (
    <Table
      t={t}
      dataSource={dataSource}
      pagination={false}
      rowKey={record => record?._id || record.monthNumber}
    >
      <Table.Column
        dataIndex={'month'}
        key="month"
        title={t('common:labels.month')}
        render={month => <Typography> {t(`common:month.${month}`)}</Typography>}
      />
      <Table.Column
        dataIndex={'memberCount'}
        key="memberCount"
        title={t('payrollAccounting:labels.memberCount')}
        render={memberCount => <Typography> {memberCount || '-'}</Typography>}
      />
      <Table.Column
        title={t('payrollAccounting:labels.file')}
        dataIndex="file"
        key="file"
        render={file => (
          <Typography>
            {file?.location ? (
              <a target="_blank" download rel="noopener noreferrer" href={file?.location}>
                {t('payrollAccounting:labels.download')}
              </a>
            ) : (
              '-'
            )}
          </Typography>
        )}
      />
      <Table.Column
        title={t('common:labels.status')}
        dataIndex="status"
        key="status"
        render={status => {
          const { background, fontColor } = PAYROLL_ACCOUNTING_STATUSES_STYLES[
            status as PayrollAccountingStatusesEnum
          ];

          return (
            <Tag background={background} fontColor={fontColor}>
              {t(`payrollAccounting:labels.statuses.${status}`)}
            </Tag>
          );
        }}
      />

      <Table.Column
        title={t('common:labels.action')}
        width={50}
        render={({ monthDate, _id }) => (
          <Popover
            elements={[
              {
                label: t(`payrollAccounting:labels.${_id ? 'updateFile' : 'createFile'}`),
                icon: ICONS.edit,
                key: ICONS.edit,
                handler: handleFileCreate({ _id, month: monthDate }),
              },
            ]}
          >
            <Icon
              icon={ICONS.dotsVerticalRounded}
              size={22}
              color={PLATFORM_THEME_COLORS.primary.grey}
              rightMargin={false}
            />
          </Popover>
        )}
      />
    </Table>
  );
};

export default PayrollAccountingDatevTable;
